import React, { useState } from 'react';

import { AutoAwesome } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid2';

import {
  Creature,
  Encounter,
  Environment,
} from "@dndModels";
import { CREATURE_TYPES } from "@dndModels/Constants";

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";
import {
  ENCOUNTER_DIFFICULTIES,
  ENCOUNTER_TYPES,
  EncounterDifficulty,
  EncounterSuggestion,
  EncounterType,
  GenerateResult,
  Thresholds,
  generateEncounterSuggestions,
  maxCreatureTypes,
} from "@dndUtilities/encounter_generator";
import { AddingCreature } from "@dndUtilities/encounter_participants";

import ModalOrDrawer from "@components/display/ModalOrDrawer";
import LoadingBox from "@components/input/LoadingBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  encounter: Encounter;
  party_color: string;
  party_thresholds?: Thresholds;
  existing_enemy_xp: number; // XP already opposing the party's team
  onEnvironmentsChange: (environment_ids: string[]) => void;
  onApply: (adding: AddingCreature[]) => Promise<void>;
}

const crString = (cr: number) => {
  switch (cr) {
    case 0.125: return "1/8";
    case 0.25: return "1/4";
    case 0.5: return "1/2";
    default: return `${cr}`;
  }
}

export default function EncounterSuggestButton({
  encounter,
  party_color,
  party_thresholds,
  existing_enemy_xp,
  onEnvironmentsChange,
  onApply,
}: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [creatures, setCreatures] = useState<Creature[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [environment_ids, setEnvironmentIDs] = useState<string[]>([]);
  const [difficulty, setDifficulty] = useState<EncounterDifficulty>("Medium");
  const [encounter_type, setEncounterType] = useState<EncounterType>("Random");
  const [creature_types, setCreatureTypes] = useState<string[]>([]);
  const [result, setResult] = useState<GenerateResult | null>(null);

  const openModal = async () => {
    setOpen(true);
    setEnvironmentIDs(encounter.environment_ids);
    if (creatures.length === 0) {
      setLoading(true);
      const res = await api.getSetOfObjects({ data_types: ["creature","environment"] });
      setCreatures((res.creature as Creature[]).filter(o => o.is_dominant));
      setEnvironments(res.environment as Environment[]);
      setLoading(false);
    }
  }

  const generate = () => {
    setResult(generateEncounterSuggestions({
      creatures: creatures.map(o => ({
        _id: o._id,
        name: o.name,
        creature_type: o.creature_type,
        challenge_rating: o.challenge_rating,
        min_challenge_rating: o.min_challenge_rating,
        max_challenge_rating: o.max_challenge_rating,
        environments: o.environments,
      })),
      known_environment_ids: environments.map(o => o._id),
      environment_ids,
      creature_types,
      difficulty,
      encounter_type,
      party_color,
      party_thresholds: party_thresholds ?? { easy: 0, medium: 0, hard: 0, deadly: 0 },
      existing_enemy_xp,
      cr_to_xp: common_util.cr_to_xp_map,
      level_thresholds: common_util.level_to_xp_thresholds,
    }));
  }

  const apply = async (suggestion: EncounterSuggestion) => {
    setApplying(true);
    const adding: AddingCreature[] = [];
    for (const group of suggestion.groups) {
      for (const member of group.members) {
        const creature = creatures.find(o => o._id === member.creature_id);
        if (creature) {
          adding.push({ creature, count: member.count, color: group.color, cr: member.cr });
        }
      }
    }
    await onApply(adding);
    setApplying(false);
    setResult(null);
    setOpen(false);
  }

  const renderSuggestion = (suggestion: EncounterSuggestion, index: number) => {
    const opposing = new Set(suggestion.groups.filter(o => !o.ally).map(o => o.color)).size > 1;
    return (
      <Grid key={index} size={12} className="padded bordered">
        <Grid container spacing={0} direction="row">
          <Grid size={9} className="header">
            { suggestion.encounter_type }
          </Grid>
          <Grid size={3}>
            <DandDButtonBox
              name="Use This"
              disabled={applying}
              onClick={() => apply(suggestion)}
            />
          </Grid>
          { suggestion.groups.map((group, g) => (
            <Grid key={g} size={12} className="padded">
              <span style={{ display: "inline-block", width: 12, height: 12, backgroundColor: group.color, border: "1px solid gray", marginRight: 6 }} />
              <b>{ group.label }</b> ({ group.xp } XP)
              { group.members.map((member, m) => (
                <div key={m} style={{ marginLeft: 18 }}>
                  { member.count } × { member.name } (CR { crString(member.cr) }, { member.creature_type })
                </div>
              ))}
            </Grid>
          ))}
          <Grid size={12} className="small_label gray">
            Enemy XP: { suggestion.enemy_xp }
            { opposing && ". The difficulty bar will read higher, since it doesn't know these groups also fight each other." }
          </Grid>
        </Grid>
      </Grid>
    );
  }

  const renderExtras = () => {
    if (!open) {
      return null;
    }
    const too_many_types = creature_types.length > maxCreatureTypes(encounter_type);
    return (
      <ModalOrDrawer
        open={open}
        width={800}
        onClose={() => { setOpen(false); }}>
        { loading || applying ?
          <LoadingBox message={applying ? "Adding Creatures" : "Loading Creatures"} />
        :
          <Grid container spacing={1} direction="row">
            <Grid size={12} className="header centered">
              Suggest Creatures
            </Grid>
            <Grid size={12}>
              <SelectObjectBox
                name="Environments"
                data_type="environment"
                values={environment_ids}
                multiple
                onChange={(changed: string[]) => {
                  setEnvironmentIDs(changed);
                  onEnvironmentsChange(changed);
                }}
              />
            </Grid>
            <Grid size={6}>
              <SelectStringBox
                name="Difficulty"
                options={[...ENCOUNTER_DIFFICULTIES]}
                value={difficulty}
                auto_sort={false}
                onChange={(changed: string) => setDifficulty(changed as EncounterDifficulty)}
              />
            </Grid>
            <Grid size={6}>
              <SelectStringBox
                name="Encounter Type"
                options={[...ENCOUNTER_TYPES]}
                value={encounter_type}
                auto_sort={false}
                onChange={(changed: string) => setEncounterType(changed as EncounterType)}
              />
            </Grid>
            <Grid size={12}>
              <SelectStringBox
                name="Creature Types to Include"
                options={CREATURE_TYPES}
                values={creature_types}
                multiple
                onChange={setCreatureTypes}
              />
            </Grid>
            { existing_enemy_xp > 0 &&
              <Grid size={12} className="small_label gray">
                The encounter already has { existing_enemy_xp } XP of creatures opposing the selected team, and suggestions account for it.
              </Grid>
            }
            { too_many_types &&
              <Grid size={12}>
                { encounter_type } can only include { maxCreatureTypes(encounter_type) } creature type.
              </Grid>
            }
            <Grid size={12}>
              <DandDButtonBox
                name={result ? "Generate New Suggestions" : "Generate Suggestions"}
                disabled={!party_thresholds || too_many_types}
                onClick={generate}
              />
            </Grid>
            { !party_thresholds &&
              <Grid size={12}>
                Add the party to the encounter first so there&apos;s something to measure difficulty against.
              </Grid>
            }
            { result && result.error &&
              <Grid size={12}>
                { result.error }
              </Grid>
            }
            { result && result.suggestions.map(renderSuggestion) }
          </Grid>
        }
      </ModalOrDrawer>
    );
  }

  return (
    <>
      <Tooltip title="Suggest Creatures">
        <AutoAwesome className="clickable" onClick={openModal} />
      </Tooltip>
      { renderExtras() }
    </>
  );
}
